import React, { useState, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { fetchFlights } from '../../../lib/queries'
import { ticketSchema } from './schema'
import moment from 'moment'
import Select from 'react-select'

function InventoryModal({ isOpen, onClose, inventoryData, mode, onSubmit }) {
    const [flights, setFlights] = useState([])
    const [loading, setLoading] = useState(true)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitError, setSubmitError] = useState(null)
    const [isNextDay, setIsNextDay] = useState(false)

    const {
        register,
        handleSubmit,
        reset,
        control,
        formState: { errors }
    } = useForm({
        resolver: yupResolver(ticketSchema),
        context: { isNextDay }
    })

    // Load flights and set initial form values
    useEffect(() => {
        const loadFlights = async () => {
            try {
                const flightData = await fetchFlights()
                setFlights(flightData.items)
            } catch (error) {
                console.error("Error loading flights:", error)
                setFlights([])
            } finally {
                setLoading(false)
            }
        }

        if (isOpen) {
            loadFlights()
            if (inventoryData) {
                const hasNextDay = inventoryData.arrivalTime?.includes('(+1)') || false
                setIsNextDay(hasNextDay)

                reset({
                    ...inventoryData,
                    arrivalTime: inventoryData.arrivalTime?.replace('(+1)', '').trim()
                })
            } else {
                reset({
                    flightNo: null,
                    departureDate: '',
                    departureTime: '',
                    arrivalTime: '',
                    availableTickets: '',
                    price: ''
                })
                setIsNextDay(false)
                setSubmitError(null)
            }
        }
    }, [isOpen, inventoryData, reset])

    const flightOptions = flights.map(flight => ({
        value: flight.id,
        label: `${flight.flightNo} (${flight.departure} - ${flight.arrival})`
    }))

    const handleFormSubmit = async (data) => {
        try {
            setIsSubmitting(true)
            setSubmitError(null)

            // Find the selected flight
            const selectedFlight = flights.find(f => f.id === data.flightNo)
            if (!selectedFlight) {
                throw new Error("Please select a valid flight")
            }

            const transformedData = {
                ...data,
                flightNo: selectedFlight.flightNo,
                departure: selectedFlight.departure,
                arrival: selectedFlight.arrival,
                arrivalTime: isNextDay ? `${data.arrivalTime} (+1)` : data.arrivalTime
            }

            await onSubmit(transformedData)
            onClose()
        } catch (error) {
            setSubmitError(error.message || 'Failed to submit form')
        } finally {
            setIsSubmitting(false)
        }
    }

    // Reset form when modal closes
    if (!isOpen) return null


    return (
        <>
            <div className="modal-backdrop show"></div>
            <div className="modal d-block show" tabIndex="-1">
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                {mode === 'update' ? 'Update Inventory' : 'Add Inventory'}
                            </h5>
                            <button type="button" className="btn-close" onClick={onClose}></button>
                        </div>
                        <div className="modal-body">
                            {loading ? (
                                <div className="text-center">Loading flights...</div>
                            ) : (
                                <form onSubmit={handleSubmit(handleFormSubmit)}>
                                    {submitError && (
                                        <div className="alert alert-danger" role="alert">
                                            {submitError}
                                        </div>
                                    )}

                                    <div className="mb-3">
                                        <label className="form-label">Flight Number:</label>
                                        <Controller
                                            name="flightNo"
                                            control={control}
                                            render={({ field }) => (
                                                <Select
                                                    {...field}
                                                    options={flightOptions}
                                                    value={flightOptions.find(option => option.value === field.value?.value || option.value === field.value)}
                                                    onChange={(selected) => field.onChange(selected)}
                                                    className={errors.flightNo ? 'is-invalid' : ''}
                                                    placeholder="Select a flight"
                                                    isClearable
                                                    isDisabled={isSubmitting}
                                                />
                                            )}
                                        />
                                        {errors.flightNo && (
                                            <div className="invalid-feedback d-block">{errors.flightNo.message}</div>
                                        )}
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Departure Date:</label>
                                        <input
                                            type="date"
                                            className={`form-control ${errors.departureDate ? 'is-invalid' : ''}`}
                                            min={moment().format('YYYY-MM-DD')}
                                            {...register('departureDate')}
                                        />
                                        {errors.departureDate && (
                                            <div className="invalid-feedback">{errors.departureDate.message}</div>
                                        )}
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Departure Time:</label>
                                        <input
                                            type="time"
                                            className={`form-control ${errors.departureTime ? 'is-invalid' : ''}`}
                                            {...register('departureTime')}
                                        />
                                        {errors.departureTime && (
                                            <div className="invalid-feedback">{errors.departureTime.message}</div>
                                        )}
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Arrival Time:</label>
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="flex-grow-1">
                                                <input
                                                    type="time"
                                                    className={`form-control ${errors.arrivalTime ? 'is-invalid' : ''}`}
                                                    {...register('arrivalTime')}
                                                />
                                                {errors.arrivalTime && (
                                                    <div className="invalid-feedback">{errors.arrivalTime.message}</div>
                                                )}
                                            </div>
                                            <div className="form-check">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    id="nextDayArrival"
                                                    checked={isNextDay}
                                                    onChange={(e) => setIsNextDay(e.target.checked)}
                                                />
                                                <label className="form-check-label" htmlFor="nextDayArrival">
                                                    Next Day (+1)
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Available Tickets:</label>
                                        <input
                                            type="number"
                                            className={`form-control ${errors.availableTickets ? 'is-invalid' : ''}`}
                                            {...register('availableTickets')}
                                        />
                                        {errors.availableTickets && (
                                            <div className="invalid-feedback">{errors.availableTickets.message}</div>
                                        )}
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Price:</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className={`form-control ${errors.price ? 'is-invalid' : ''}`}
                                            {...register('price')}
                                        />
                                        {errors.price && (
                                            <div className="invalid-feedback">{errors.price.message}</div>
                                        )}
                                    </div>

                                    <div className="modal-footer">
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={onClose}
                                            disabled={isSubmitting}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    {mode === 'update' ? 'Updating...' : 'Adding...'}
                                                </>
                                            ) : (
                                                mode === 'update' ? 'Update' : 'Add'
                                            )}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default InventoryModal